import React from 'react';

// Por qué un día sale "No disponible" en los calendarios de "Nuevo turno"
// (secretaria y profesional). El motivo lo calcula el backend en la ruta del
// panel (/available-slots, con sesión); acá solo se arma el pedido, el texto
// de respaldo y la caja que lo muestra al tocar el día. Oct-2026: salió de
// cuatro confusiones seguidas (el tope de días de Vero, el bloqueo de
// Cynthia, un horario propio olvidado y una franja más corta que el servicio).

export type MotivoDia = { codigo: string; texto: string };
export type MotivosDias = Record<string, MotivoDia>;

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DIAS_PLURAL = ['domingos', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados'];

const fechaCorta = (iso: string) => `${iso.substring(8, 10)}/${iso.substring(5, 7)}`;

/**
 * Pide los días con turnos de un servicio y, con sesión, el motivo de los que
 * no tienen. Si la ruta del panel falla (sin sesión, suscripción vencida), cae
 * a la pública: mismos días, sin motivos.
 */
export async function pedirDiasDelServicio(
  apiUrl: string,
  professionalId: number | string,
  consulta: string
): Promise<{ fechas: string[]; motivos: MotivosDias } | null> {
  const base = `${apiUrl}/api/schedule/professional/${professionalId}`;
  const token = localStorage.getItem('token');
  const pedir = (url: string, conSesion: boolean) =>
    fetch(url, conSesion && token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  let data = token ? await pedir(`${base}/available-slots${consulta}`, true) : null;
  if (!data?.success) data = await pedir(`${base}/available-slots/public${consulta}`, false);
  if (!data?.success) return null;
  const fechas = Array.from(new Set(
    (data.slots || []).filter((sl: any) => sl.available).map((sl: any) => sl.date)
  )) as string[];
  return { fechas, motivos: data.motivos || {} };
}

/** El motivo que se muestra para un día no disponible (sin contar los pasados). */
export function motivoDelDia({ fecha, diaSemana, motivos, maxDate, bloqueada, soloEspeciales, diaLaboral }: {
  fecha: string;
  diaSemana: number;
  motivos: MotivosDias;
  maxDate?: string;
  bloqueada: boolean;
  soloEspeciales: boolean;
  diaLaboral: boolean;
}): string {
  if (maxDate && fecha > maxDate) {
    return `Pasa del límite de reservas: se puede reservar hasta el ${fechaCorta(maxDate)}. Se cambia en Horarios → Reservas online.`;
  }
  if (motivos[fecha]) return motivos[fecha].texto;
  if (bloqueada) return 'Día cerrado. Se carga en Horarios → Bloqueos.';
  if (soloEspeciales) return 'Solo se atiende en días especiales (está activado en Horarios).';
  if (!diaLaboral) return `Los ${DIAS_PLURAL[diaSemana]} no se atiende. Para atender igual: "Abrir día" en la agenda.`;
  return 'Ese día no quedan horarios.';
}

/** La caja debajo del calendario con el motivo del día tocado. */
export const CajaMotivo: React.FC<{ fecha: string; texto: string; onCerrar: () => void }> = ({ fecha, texto, onCerrar }) => {
  const [a, m, d] = fecha.split('-').map(Number);
  const dia = DIAS[new Date(a, m - 1, d).getDay()];
  // Los calendarios están adentro de modales con scroll: que se vea sin buscarla.
  // scroll-mb deja lugar para la barra fija de abajo del panel en el celular.
  const caja = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    caja.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [fecha, texto]);
  return (
    <div ref={caja} role="status" className="mt-2 scroll-mt-24 scroll-mb-28 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left text-xs text-amber-900">
      <p className="flex-1 leading-snug">
        <span className="font-semibold">{dia} {fechaCorta(fecha)}:</span> {texto}
      </p>
      <button
        type="button"
        onClick={onCerrar}
        className="shrink-0 rounded px-1 text-amber-700 hover:bg-amber-100"
        aria-label="Cerrar el motivo"
      >
        ×
      </button>
    </div>
  );
};
